import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { getSignedUrl } from '../../utils/fileUtils';
import './FileViewer.css';

interface FileViewerProps {
  /** CRM file_object id — used to fetch signed URL */
  fileId?: number;
  /** Direct URL (e.g. Google Drive preview) — skips signed URL fetch */
  directUrl?: string;
  filename: string;
  mimeType?: string;
  onClose: () => void;
}

/**
 * Full-screen file viewer overlay.
 * Renders PDFs & images natively via <iframe>/<img>.
 * Falls back to download prompt for other types.
 * Esc or clicking backdrop closes.
 */
function FileViewer({ fileId, directUrl, filename, mimeType, onClose }: FileViewerProps) {
  const [url, setUrl] = useState<string | null>(directUrl || null);
  const [loading, setLoading] = useState(!directUrl);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // If directUrl provided, no need to fetch signed URL
    if (directUrl) {
      setUrl(directUrl);
      setLoading(false);
      return;
    }
    if (!fileId) {
      setError('No file specified');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const signed = await getSignedUrl(fileId);
      if (cancelled) return;
      if (!signed) {
        setError('Could not load file');
        setLoading(false);
        return;
      }
      setUrl(signed);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fileId, directUrl]);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleDownload = useCallback(async () => {
    const downloadUrl = url || (fileId ? await getSignedUrl(fileId) : null);
    if (!downloadUrl) {
      toast.error('Could not download file');
      return;
    }
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [url, fileId, filename]);

  const handleShare = useCallback(async () => {
    const shareUrl = url || (fileId ? await getSignedUrl(fileId) : null);
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: filename, url: shareUrl });
      } catch {
        // User cancelled share
      }
    } else {
      await navigator.clipboard?.writeText(shareUrl);
      toast.success('Link copied to clipboard');
    }
  }, [url, fileId, filename]);

  const handleOpenTab = useCallback(() => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  const isPdf = mimeType?.includes('pdf') || filename.toLowerCase().endsWith('.pdf');
  const isImage = mimeType?.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(filename);

  return (
    <div className="sky-fv-overlay" onClick={onClose}>
      {/* Toolbar */}
      <div className="sky-fv-toolbar" onClick={(e) => e.stopPropagation()}>
        <div className="sky-fv-toolbar__info">
          <FileIcon />
          <span className="sky-fv-toolbar__name">{filename}</span>
        </div>
        <div className="sky-fv-toolbar__actions">
          <button className="sky-fv-toolbar__btn" onClick={handleOpenTab} title="Open in new tab">
            <ExternalLinkIcon />
          </button>
          <button className="sky-fv-toolbar__btn" onClick={handleShare} title="Share / Copy link">
            <ShareIcon />
          </button>
          <button className="sky-fv-toolbar__btn" onClick={handleDownload} title="Download">
            <DownloadIcon />
          </button>
          <button className="sky-fv-toolbar__btn sky-fv-toolbar__btn--close" onClick={onClose} title="Close (Esc)">
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Content area — fills the space under the toolbar, so the dark area
          around the document is this element, not the overlay. Clicking that
          empty area must close the viewer; clicks on the document itself and
          its children must not. */}
      <div
        className="sky-fv-content"
        onClick={(e) => {
          e.stopPropagation();
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {loading && (
          <div className="sky-fv-loading">
            <div className="sky-fv-spinner" />
            <span>Loading {filename}…</span>
          </div>
        )}
        {error && (
          <div className="sky-fv-error">
            <span>{error}</span>
            {fileId && (
              <button className="sky-fv-error__retry" onClick={() => { setLoading(true); setError(null); getSignedUrl(fileId).then(u => { setUrl(u); setLoading(false); if (!u) setError('Could not load file'); }); }}>
                Retry
              </button>
            )}
          </div>
        )}
        {!loading && !error && url && isPdf && (
          <iframe
            src={url}
            className="sky-fv-iframe"
            title={filename}
          />
        )}
        {!loading && !error && url && isImage && (
          <img
            src={url}
            className="sky-fv-image"
            alt={filename}
          />
        )}
        {!loading && !error && url && !isPdf && !isImage && (
          <div className="sky-fv-unsupported">
            <FileIcon large />
            <span className="sky-fv-unsupported__name">{filename}</span>
            <span className="sky-fv-unsupported__hint">Preview not available for this file type</span>
            <button className="sky-fv-unsupported__download" onClick={handleDownload}>
              <DownloadIcon /> Download
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Icons ───────────────────────────────────────────── */

function FileIcon({ large }: { large?: boolean }) {
  const s = large ? 40 : 16;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="3" y2="15" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" x2="21" y1="14" y2="3" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" x2="6" y1="6" y2="18" />
      <line x1="6" x2="18" y1="6" y2="18" />
    </svg>
  );
}

export default React.memo(FileViewer);
